import { create } from "zustand";
import { SectionEvent, SectionStatusEntry } from "~/types/section-streaming";
import { sectionEventToStatus } from "~/helpers/sectionEvents.helper";

/**
 * Section_Status store — transient, in-memory map of per-section streaming
 * status (`idle | loading | ready | error`) fed by additive Section_Events
 * from the status WebSocket.
 *
 * Deliberately NOT persisted: the partial preview is ephemeral (design D3) and
 * persisting any of it to localStorage/sessionStorage is disallowed in this
 * domain — este store usa `create()` puro, sem o middleware `persist`.
 *
 * Além do status, guarda o CONTEÚDO da seção (`contents`), estritamente em
 * memória. Antes o conteúdo que vinha em `event.section` era descartado aqui,
 * então o preview parcial só conseguia produzir um spinner com o rótulo da
 * seção — os dados chegavam pelo WebSocket, eram parseados e morriam no store,
 * e a tela ficava "carregando" indefinidamente. Guardá-lo é o que permite a
 * renderização progressiva; ele desaparece no reload junto com o resto do
 * store, que é exatamente a semântica efêmera exigida pelo D3.
 */
interface SectionStatusActions {
  /** Apply a parsed Section_Event, advancing the section's status. */
  applySectionEvent: (event: SectionEvent) => void;
  /** Clear all tracked section statuses (e.g. when a new stream starts). */
  resetSectionStatuses: () => void;
  /**
   * Discard the ephemeral per-section preview when the FINAL report is
   * processed, so the final render replaces the streaming skeletons/preview
   * (Feature: redes-sociais-report, Task 7.4 — Requirement 6.10).
   *
   * Scoped to the report whose sections are being previewed: clears only when
   * `userReportsId` matches the tracked `streamingReportId` (or when the stream
   * never carried a report id — a single active stream is assumed to be this
   * one). A concurrent completion of a DIFFERENT report therefore never wipes an
   * in-flight preview. Returns `true` iff a preview was actually discarded, so
   * the caller can trigger the final re-render only for the previewed report.
   */
  clearPreviewForReport: (userReportsId?: string) => boolean;
}

interface SectionStatusState {
  /** Status map keyed by `section_id`. */
  statuses: Record<string, SectionStatusEntry>;
  /**
   * Conteúdo da seção (front-format) EM MEMÓRIA, indexado por
   * `user_reports_id` -> `section_id`.
   *
   * O escopo por relatório NÃO é decorativo. `section_id` é
   * `subsection/title`, o que desambigua por REDE mas não por RELATÓRIO — e as
   * seções agnósticas de rede ("URLs", "Contatos — E-mails") têm `subsection`
   * vazio, gerando `section_id` IDÊNTICO em relatórios diferentes. Com um mapa
   * plano, gerar um relatório de Facebook e um de Instagram fazia o segundo
   * sobrescrever as seções do primeiro: o usuário via dados de um relatório
   * dentro do outro.
   *
   * Eventos sem `user_reports_id` (o contrato permite omitir) caem em
   * `UNSCOPED_REPORT_KEY` e só são usados como fallback quando não há conteúdo
   * específico do relatório aberto.
   */
  contents: Record<string, Record<string, unknown>>;
  /** Ordem de primeira aparição das seções, por relatório. */
  contentOrder: Record<string, string[]>;
  /** Section ids in first-seen order, for deterministic rendering. */
  order: string[];
  /**
   * Owning report id of the ephemeral preview currently being streamed, if
   * known. Captured from Section_Events that carry `user_reports_id` so the
   * preview→final swap (Task 7.4) can be scoped to the right report. Never
   * persisted — like the rest of this store it lives only in memory (design D3).
   */
  streamingReportId?: string;
  actions: SectionStatusActions;
}

/**
 * Bucket para eventos que não informam `user_reports_id`. O contrato permite
 * omitir; nesse caso assume-se um único stream ativo.
 */
export const UNSCOPED_REPORT_KEY = "__unscoped__";

/**
 * Leitura segura de um mapa indexado por id de relatório.
 *
 * `map[key]` NÃO basta: o id vem de um evento de WebSocket e chaves herdadas
 * de `Object.prototype` ("toString", "constructor", "valueOf"…) devolvem a
 * função do protótipo em vez de `undefined`, então o `?? fallback` não
 * dispara e o valor vira algo que não é array/objeto. Um teste de propriedade
 * pegou exatamente isso com `user_reports_id = "toString"`.
 */
function ownOrDefault<T>(map: Record<string, T>, key: string, fallback: T): T {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : fallback;
}

const INITIAL_STATE: Pick<
  SectionStatusState,
  "statuses" | "contents" | "contentOrder" | "order" | "streamingReportId"
> = {
  statuses: {},
  contents: {},
  contentOrder: {},
  order: [],
  streamingReportId: undefined,
};

export const useSectionStatusStore = create<SectionStatusState>((set, get) => ({
  ...INITIAL_STATE,
  actions: {
    applySectionEvent: (event) =>
      set((state) => {
        const status = sectionEventToStatus(event.event);
        const existing = state.statuses[event.section_id];
        const reportKey = event.user_reports_id || UNSCOPED_REPORT_KEY;
        const existingOrder = ownOrDefault(state.contentOrder, reportKey, [] as string[]);
        const reportOrder = existingOrder.includes(event.section_id)
          ? existingOrder
          : [...existingOrder, event.section_id];
        const order = state.order.includes(event.section_id)
          ? state.order
          : [...state.order, event.section_id];

        return {
          statuses: {
            ...state.statuses,
            [event.section_id]: {
              status,
              // Keep the last known label when an event omits it.
              label: event.section_label ?? existing?.label,
            },
          },
          // Só grava quando o evento traz conteúdo; `started` e afins
          // preservam o que já foi renderizado. O escopo por relatório impede
          // que um `section_id` repetido entre relatórios (ex.: "URLs", que
          // não tem rede no id) sobrescreva o do outro.
          contents:
            event.section === undefined
              ? state.contents
              : {
                  ...state.contents,
                  [reportKey]: {
                    ...ownOrDefault(state.contents, reportKey, {} as Record<string, unknown>),
                    [event.section_id]: event.section,
                  },
                },
          contentOrder:
            event.section === undefined
              ? state.contentOrder
              : {
                  ...state.contentOrder,
                  [reportKey]: reportOrder,
                },
          order,
          // Remember which report this ephemeral preview belongs to. Retain the
          // last known id when an event omits it.
          streamingReportId: event.user_reports_id ?? state.streamingReportId,
        };
      }),
    resetSectionStatuses: () => set({ ...INITIAL_STATE }),
    clearPreviewForReport: (userReportsId) => {
      const state = get();
      // Nothing streaming: no ephemeral preview to discard. Also makes this a
      // no-op for report types that never emit Section_Events.
      if (state.order.length === 0) {
        return false;
      }
      // Only discard the preview that belongs to the completed report. When the
      // stream did carry a report id and it differs, keep the preview intact.
      if (
        state.streamingReportId &&
        userReportsId &&
        state.streamingReportId !== userReportsId
      ) {
        return false;
      }
      set({ ...INITIAL_STATE });
      return true;
    },
  },
}));

export const useSectionStatuses = () =>
  useSectionStatusStore((state) => state.statuses);

/** Mapa completo `reportId -> sectionId -> conteúdo` (efêmero). */
export const useSectionContents = () =>
  useSectionStatusStore((state) => state.contents);

/** Ordem de primeira aparição das seções, por relatório. */
export const useSectionContentOrder = () =>
  useSectionStatusStore((state) => state.contentOrder);

export const useSectionStatusOrder = () =>
  useSectionStatusStore((state) => state.order);

export const useStreamingReportId = () =>
  useSectionStatusStore((state) => state.streamingReportId);

export const useSectionStatusActions = () =>
  useSectionStatusStore((state) => state.actions);
